import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchServices,
  createService,
  updateService,
  hardDeleteService,
  setServiceActive,
} from "../../store/slices/serviceSlice";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const KATEGORI_ONERILERI = [
  "Saç",
  "Renklendirme",
  "Cilt Bakımı",
  "Makyaj",
  "Tırnak & Spa",
  "Masaj",
  "Epilasyon",
  "Danışmanlık",
];

const fmtMoney = (v) =>
  v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—";

const emptyForm = {
  name: "",
  category: "",
  durationMinutes: "60",
  price: "",
  commissionRate: "",
  description: "",
};

const parseServicesCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) throw new Error("Dosya boş veya başlık satırı eksik.");

  const firstLine = lines[0];
  const delimiter = firstLine.includes(";") ? ";" : ",";
  const headers = firstLine.split(delimiter).map((h) => h.trim().toLowerCase());

  const nameIdx = headers.findIndex((h) => h.includes("ad") || h.includes("name") || h.includes("hizmet"));
  const catIdx = headers.findIndex((h) => h.includes("kategori") || h.includes("category"));
  const durIdx = headers.findIndex((h) => h.includes("süre") || h.includes("duration"));
  const priceIdx = headers.findIndex((h) => h.includes("fiyat") || h.includes("price") || h.includes("ücret"));
  const commIdx = headers.findIndex((h) => h.includes("prim") || h.includes("commission"));
  const descIdx = headers.findIndex((h) => h.includes("açıklama") || h.includes("description") || h.includes("tanım"));

  if (nameIdx === -1) throw new Error("Hizmet adı sütunu bulunamadı.");
  if (priceIdx === -1) throw new Error("Fiyat sütunu bulunamadı.");

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(delimiter).map((c) => c.trim().replace(/^"|"$/g, ""));
    if (cols.length === 0 || (cols.length === 1 && cols[0] === "")) continue;

    const name = cols[nameIdx] || "";
    const category = catIdx !== -1 ? cols[catIdx] : "";
    const durationMinutes = durIdx !== -1 ? parseInt(cols[durIdx], 10) : 60;
    const priceStr = priceIdx !== -1 ? cols[priceIdx] : "";
    const priceClean = priceStr.replace(/[^\d.,]/g, "").replace(",", ".");
    const price = parseFloat(priceClean);

    const commStr = commIdx !== -1 ? cols[commIdx] : "";
    const commissionRate = commStr ? parseFloat(commStr.replace(/[^\d.,]/g, "").replace(",", ".")) : null;
    const description = descIdx !== -1 ? cols[descIdx] : "";

    const errors = [];
    if (!name) errors.push("Hizmet adı eksik.");
    if (isNaN(price) || price < 0) errors.push("Geçersiz fiyat.");
    if (isNaN(durationMinutes) || durationMinutes < 1) errors.push("Geçersiz süre.");
    if (commissionRate !== null && (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100)) {
      errors.push("Geçersiz prim oranı (%0-100 olmalı).");
    }

    rows.push({
      id: i,
      name,
      category,
      durationMinutes: isNaN(durationMinutes) ? 60 : durationMinutes,
      price: isNaN(price) ? 0 : price,
      commissionRate,
      description,
      errors,
    });
  }

  return rows;
};

export default function ServicesPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, isLoading, error } = useSelector((state) => state.services);
  const user = useSelector((state) => state.auth.user);
  const isManager = user?.role === "MANAGER";
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importProgress, setImportProgress] = useState(null); // { current, total, errors }
  const [importStatus, setImportStatus] = useState("select"); // "select" | "preview" | "importing" | "done"
  const [dragActive, setDragActive] = useState(false);
  const [importFileError, setImportFileError] = useState(null);

  const downloadSampleCSV = () => {
    const csvContent = "\uFEFF" +
      "Hizmet Adı;Kategori;Süre (dk);Fiyat (₺);Prim Oranı (%);Açıklama\n" +
      "Saç Kesimi & Yıkama;Saç;45;250,00;10;Yıkama dahil fön hariç saç kesimi\n" +
      "Fön Çekimi;Saç;20;100,00;;Günlük fön\n" +
      "Cilt Bakımı Klasik;Cilt Bakımı;60;500,00;15;Maske ve nemlendirme\n" +
      "Manikür Klasik;Tırnak & Spa;30;150,00;10;Tırnak bakımı ve törpü\n";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "ornek_hizmet_listesi.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file) => {
    setImportFileError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseServicesCSV(text);
        setImportRows(parsed);
        setImportStatus("preview");
      } catch (err) {
        setImportFileError(err.message || "Dosya okunamadı.");
      }
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleStartImport = async () => {
    const validRows = importRows.filter((r) => r.errors.length === 0);
    if (validRows.length === 0) {
      showToast("İçe aktarılacak geçerli satır bulunamadı.", "error");
      return;
    }

    setImportStatus("importing");
    setImportProgress({ current: 0, total: validRows.length, errors: [] });

    let successCount = 0;
    const errorsList = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const payload = {
        name: row.name.trim(),
        category: row.category.trim() || null,
        durationMinutes: row.durationMinutes !== "" ? Number(row.durationMinutes) : null,
        price: Number(row.price),
        commissionRate: row.commissionRate !== null ? Number(row.commissionRate) : null,
        description: row.description.trim() || null,
      };

      try {
        await dispatch(createService(payload)).unwrap();
        successCount++;
      } catch (err) {
        const errMsg = typeof err === "string" ? err : "Bilinmeyen hata.";
        errorsList.push({ name: row.name, error: errMsg });
      }

      setImportProgress((prev) => ({
        ...prev,
        current: i + 1,
        errors: [...errorsList],
      }));
    }

    setImportStatus("done");
    if (successCount > 0) {
      showToast(`${successCount} hizmet başarıyla içe aktarıldı.`, "success");
      refresh();
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportRows([]);
    setImportProgress(null);
    setImportStatus("select");
    setImportFileError(null);
  };

  useEffect(() => {
    dispatch(fetchServices({ size: 200 }));
  }, [dispatch]);

  const refresh = () => dispatch(fetchServices({ size: 200 }));

  const kategoriler = useMemo(() => {
    const set = new Set(KATEGORI_ONERILERI);
    items.forEach((s) => s.category && set.add(s.category));
    return Array.from(set);
  }, [items]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? "",
      category: row.category ?? "",
      durationMinutes: row.durationMinutes != null ? String(row.durationMinutes) : "",
      price: row.price != null ? String(row.price) : "",
      commissionRate: row.commissionRate != null ? String(row.commissionRate) : "",
      description: row.description ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (form.price === "" || Number(form.price) < 0) {
      setFormError("Fiyat zorunludur.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category.trim() || null,
      durationMinutes: form.durationMinutes !== "" ? Number(form.durationMinutes) : null,
      price: Number(form.price),
      commissionRate: form.commissionRate !== "" ? Number(form.commissionRate) : null,
      description: form.description.trim() || null,
    };
    try {
      if (editingId) {
        await dispatch(updateService({ id: editingId, payload })).unwrap();
        showToast("Hizmet güncellendi.", "success");
      } else {
        await dispatch(createService(payload)).unwrap();
        showToast("Hizmet eklendi.", "success");
      }
      closeModal();
      refresh();
    } catch (err) {
      setFormError(typeof err === "string" ? err : "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const triggerHardDelete = (row) => {
    setConfirmModal({
      isOpen: true,
      title: "Hizmeti Kalıcı Sil",
      message: `"${row.name}" kalıcı olarak silinecek. Randevularda kullanılıyorsa engellenir. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        dispatch(hardDeleteService(row.id))
          .unwrap()
          .then(() => showToast("Hizmet silindi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız.", "error")),
    });
  };
  const toggleActive = (row) => {
    dispatch(setServiceActive({ id: row.id, active: !row.active }))
      .unwrap()
      .then(() => showToast(row.active ? "Pasife alındı." : "Aktifleştirildi.", "success"))
      .catch((err) => showToast(err || "İşlem başarısız.", "error"));
  };

  const columns = [
    {
      key: "name",
      header: "Hizmet",
      type: "string",
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{val}</span>
          {row.description && (
            <span className="text-[11px] text-on-surface-variant block truncate max-w-[280px]">
              {row.description}
            </span>
          )}
        </div>
      ),
    },
    { key: "category", header: "Kategori", type: "string", sortable: true, render: (v) => v || "—" },
    {
      key: "durationMinutes",
      header: "Süre",
      type: "number",
      sortable: true,
      render: (v) => (v != null ? `${v} dk` : "—"),
    },
    { key: "price", header: "Fiyat", type: "number", sortable: true, render: (v) => fmtMoney(v) },
    {
      key: "commissionRate",
      header: "Prim %",
      type: "number",
      sortable: true,
      render: (v) => (v != null ? `%${v}` : "—"),
    },
    {
      key: "active",
      header: "Durum",
      sortable: true,
      render: (val) => (
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            val
              ? "bg-success/15 text-success border border-success/20"
              : "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40"
          }`}
        >
          {val ? "Aktif" : "Pasif"}
        </span>
      ),
    },
  ];

  const renderActions = (s) => (
    <div className="flex items-center justify-end gap-1.5">
      <button
        onClick={() => openEdit(s)}
        title="Düzenle"
        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
      >
        <Icon name="edit" className="!text-[18px]" />
      </button>
      {isManager && (
        <>
          <button
            onClick={() => toggleActive(s)}
            title={s.active ? "Pasife Al" : "Aktifleştir"}
            className={`p-1.5 rounded-lg transition ${
              s.active ? "text-tier-gold hover:bg-tier-gold/10" : "text-success hover:bg-success/10"
            }`}
          >
            <Icon name={s.active ? "pause_circle" : "play_circle"} className="!text-[18px]" />
          </button>
          <button
            onClick={() => triggerHardDelete(s)}
            title="Kalıcı Sil"
            className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
          >
            <Icon name="delete_forever" className="!text-[18px]" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="content_cut" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Hizmetler</h1>
            <p className="text-on-surface-variant text-sm">
              Hizmetleri ve fiyatlarını dinamik olarak yönetin.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setImportModalOpen(true)}
            className="border border-outline-variant bg-white text-on-surface rounded-xl px-4 py-2 font-semibold text-sm hover:bg-surface-container-high transition flex items-center gap-1.5"
          >
            <Icon name="upload" className="!text-[18px]" /> Excel / CSV İçe Aktar
          </button>
          <button
            onClick={openCreate}
            className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition flex items-center gap-1.5"
          >
            <Icon name="add" className="!text-[18px]" /> Yeni Hizmet
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={items}
          actions={renderActions}
          searchPlaceholder="Hizmette ara..."
        />
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="content_cut" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">
                  {editingId ? "Hizmeti Düzenle" : "Yeni Hizmet"}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Hizmet Adı *</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Kategori</label>
                  <input
                    list="kategori-list"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Örn. Saç"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                  <datalist id="kategori-list">
                    {kategoriler.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Süre (dk)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.durationMinutes}
                    onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Fiyat (₺) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Prim Oranı (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.commissionRate}
                    onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                  İptal
                </button>
                <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5">
                  {saving ? "Kaydediliyor…" : (<><Icon name="save" className="!text-[16px]" /> Kaydet</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="upload" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">
                  Hizmet Listesi İçe Aktar (CSV)
                </h3>
              </div>
              <button onClick={closeImportModal} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition" disabled={importStatus === "importing"}>
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 text-sm space-y-4">
              {importStatus === "select" && (
                <div className="space-y-6">
                  {/* File Dropzone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-3xl p-8 text-center flex flex-col items-center justify-center gap-3 transition ${
                      dragActive
                        ? "border-primary bg-primary/5"
                        : "border-outline-variant hover:border-primary/50 bg-surface-container-lowest"
                    }`}
                  >
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                      <Icon name="cloud_upload" className="!text-[32px]" />
                    </div>
                    <div>
                      <p className="font-semibold text-on-surface">
                        Dosyanızı buraya sürükleyin veya göz atın
                      </p>
                      <p className="text-xs text-on-surface-variant mt-1">
                        Sadece UTF-8 kodlamalı .csv dosyaları desteklenir
                      </p>
                    </div>
                    <label className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-xs hover:opacity-90 transition cursor-pointer mt-2">
                      Dosya Seç
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                    {importFileError && (
                      <p className="text-xs text-error mt-2 font-medium flex items-center gap-1">
                        <Icon name="error" className="!text-[14px]" /> {importFileError}
                      </p>
                    )}
                  </div>

                  {/* Instructions and Download Template */}
                  <div className="bg-surface-container-low rounded-2xl p-5 border border-outline-variant space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-on-surface flex items-center gap-1.5">
                        <Icon name="info" className="text-primary !text-[18px]" />
                        Nasıl İçe Aktarılır?
                      </h4>
                      <button
                        onClick={downloadSampleCSV}
                        className="text-primary hover:underline text-xs font-semibold flex items-center gap-1"
                      >
                        <Icon name="download" className="!text-[16px]" /> Örnek Şablon İndir (.csv)
                      </button>
                    </div>
                    <ul className="list-disc pl-5 text-xs text-on-surface-variant space-y-1.5">
                      <li>Hizmet listenizi Excel veya başka bir programda hazırladıktan sonra <strong>CSV (virgül veya noktalı virgül ayrıcı)</strong> olarak kaydedin.</li>
                      <li>Dosyanızın ilk satırında şu başlıklar bulunmalıdır: <strong>Hizmet Adı</strong>, <strong>Kategori</strong>, <strong>Süre (dk)</strong>, <strong>Fiyat (₺)</strong>, <strong>Prim Oranı (%)</strong>, <strong>Açıklama</strong>.</li>
                      <li><strong>Hizmet Adı</strong> ve <strong>Fiyat</strong> alanlarının doldurulması zorunludur. Fiyatlarda ondalık ayracı olarak virgül (,) veya nokta (.) kullanabilirsiniz.</li>
                      <li><strong>Süre</strong> belirtilmezse varsayılan olarak 60 dakika, <strong>Prim Oranı</strong> belirtilmezse varsayılan olarak %0 kabul edilir.</li>
                    </ul>
                  </div>
                </div>
              )}

              {importStatus === "preview" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-on-surface-variant">
                      Dosyadaki <strong>{importRows.length}</strong> satır analiz edildi. Geçerli satırlar içe aktarılacaktır.
                    </p>
                    {importRows.some((r) => r.errors.length > 0) && (
                      <span className="text-xs font-semibold text-error bg-error/10 px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <Icon name="warning" className="!text-[14px]" /> Hatalı satırlar tespit edildi
                      </span>
                    )}
                  </div>

                  {/* Preview Table */}
                  <div className="border border-outline-variant rounded-2xl overflow-hidden max-h-[40vh] overflow-y-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-surface-container-low text-on-surface-variant font-semibold sticky top-0">
                        <tr>
                          <th className="p-3 w-12 text-center">Durum</th>
                          <th className="p-3">Hizmet Adı</th>
                          <th className="p-3">Kategori</th>
                          <th className="p-3 w-16 text-center">Süre</th>
                          <th className="p-3 w-24 text-right">Fiyat</th>
                          <th className="p-3 w-20 text-center">Prim %</th>
                          <th className="p-3">Açıklama</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant">
                        {importRows.map((row) => (
                          <tr
                            key={row.id}
                            className={
                              row.errors.length > 0
                                ? "bg-error/5 text-error-container"
                                : "hover:bg-surface-container-high"
                            }
                          >
                            <td className="p-3 text-center">
                              {row.errors.length > 0 ? (
                                <Icon
                                  name="cancel"
                                  className="text-error !text-[16px]"
                                  title={row.errors.join("\n")}
                                />
                              ) : (
                                <Icon name="check_circle" className="text-success !text-[16px]" />
                              )}
                            </td>
                            <td className="p-3 font-semibold">
                              {row.name || <span className="italic text-error">Boş</span>}
                            </td>
                            <td className="p-3 text-on-surface-variant">{row.category || "—"}</td>
                            <td className="p-3 text-center">{row.durationMinutes} dk</td>
                            <td className="p-3 text-right font-semibold">
                              {row.price != null ? `${row.price.toFixed(2)} ₺` : "—"}
                            </td>
                            <td className="p-3 text-center">
                              {row.commissionRate != null ? `%${row.commissionRate}` : "—"}
                            </td>
                            <td className="p-3 text-on-surface-variant truncate max-w-[120px]">
                              {row.errors.length > 0 ? (
                                <span className="text-error font-medium">{row.errors.join(", ")}</span>
                              ) : (
                                row.description || "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <button
                      onClick={() => setImportStatus("select")}
                      className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs"
                    >
                      Geri Dön
                    </button>
                    <button
                      onClick={handleStartImport}
                      disabled={!importRows.some((r) => r.errors.length === 0)}
                      className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Icon name="play_arrow" className="!text-[16px]" /> İçe Aktarmayı Başlat
                    </button>
                  </div>
                </div>
              )}

              {importStatus === "importing" && importProgress && (
                <div className="py-8 text-center space-y-4">
                  <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto"></div>
                  <div className="space-y-1">
                    <p className="font-semibold text-on-surface text-base">Hizmetler İçe Aktarılıyor</p>
                    <p className="text-xs text-on-surface-variant">
                      Lütfen pencereyi kapatmayın veya sayfayı yenilemeyin.
                    </p>
                  </div>
                  <div className="max-w-md mx-auto">
                    <div className="w-full bg-outline-variant/30 h-2.5 rounded-full overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-300"
                        style={{
                          width: `${(importProgress.current / importProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-on-surface-variant mt-2">
                      <span>{importProgress.current} / {importProgress.total} Hizmet</span>
                      <span>%{Math.round((importProgress.current / importProgress.total) * 100)}</span>
                    </div>
                  </div>
                </div>
              )}

              {importStatus === "done" && importProgress && (
                <div className="space-y-4">
                  <div className="bg-success/15 border border-success/20 rounded-2xl p-5 text-center flex flex-col items-center justify-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-success/25 text-success flex items-center justify-center">
                      <Icon name="check" className="!text-[28px]" />
                    </div>
                    <h4 className="font-bold text-on-surface text-lg">Aktarım Tamamlandı!</h4>
                    <p className="text-xs text-on-surface-variant">
                      Toplam <strong>{importProgress.total - importProgress.errors.length}</strong> hizmet başarıyla eklenmiştir.
                    </p>
                  </div>

                  {importProgress.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-error flex items-center gap-1">
                        <Icon name="error" className="!text-[14px]" /> Eklenemeyen Hizmetler ({importProgress.errors.length} satır)
                      </p>
                      <div className="border border-error/25 bg-error/5 rounded-2xl p-3 max-h-[25vh] overflow-y-auto divide-y divide-error/15 text-xs text-error-container">
                        {importProgress.errors.map((err, idx) => (
                          <div key={idx} className="py-2 flex justify-between gap-4">
                            <span className="font-semibold">{err.name}</span>
                            <span className="opacity-80 text-right">{err.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <button
                      onClick={closeImportModal}
                      className="bg-primary text-white rounded-xl px-6 py-2 font-semibold text-xs hover:opacity-90 transition"
                    >
                      Kapat
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
